import React, { useState } from 'react';
import {
    Box,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert,
    Typography
} from '@mui/material';
import RootDialogPage from '../../Commun/components/RootDialogPage';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import { blobticMessage } from '../../Commun/components/BlobticMessage';
import { estimatesApi } from '../services/estimatesApi';
import { Project } from '../services/projectsApi';
import { windowsApi } from '../services/windowsApi';
import { doorsApi } from '../services/doorsApi';

interface EstimateGenerateDialogProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    projects: Project[];
}

export const EstimateGenerateDialog: React.FC<EstimateGenerateDialogProps> = ({
    open,
    onClose,
    onSuccess,
    projects
}) => {
    const [selectedProjectId, setSelectedProjectId] = useState<string>('');
    const [generating, setGenerating] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const { t } = useLanguage();
    

    const handleGenerateEstimate = async () => {
        if (!selectedProjectId) {
            setError(t('maker.design.validation.project'));
            return;
        }

        try {
            setGenerating(true);
            setError(null);

            // Fetch project items to calculate total
            const projectId = parseInt(selectedProjectId);
            const [windowsRes, doorsRes] = await Promise.all([
                windowsApi.getByProject(projectId),
                doorsApi.getByProject(projectId)
            ]);

            const windows = windowsRes.data || [];
            const doors = doorsRes.data || [];

            let totalAmount = 0;

            const calculateItemPrice = (item: any) => {
                let price = 0;
                if (item.customShapeData) {
                    try {
                        const data = JSON.parse(item.customShapeData);
                        if (data.quotation) {
                            price = (data.quotation.unitPrice || 0) * (data.quotation.quantity || 1);
                            // Subtract discount from price if needed, logic depends on requirement
                            // Assuming unitPrice is net for now or simple sum
                            if (data.quotation.discount) {
                                price = price - (price * (data.quotation.discount / 100));
                            }
                        }
                    } catch (e) {
                        console.warn('Failed to parse item data for price', e);
                    }
                }
                return price;
            };

            windows.forEach(w => totalAmount += calculateItemPrice(w));
            doors.forEach(d => totalAmount += calculateItemPrice(d));

            // Default Tax Rate (could be param)
            const taxRate = 19;

            await estimatesApi.generate(projectId, totalAmount, taxRate);
            onSuccess();
            onClose();
            setSelectedProjectId('');
            blobticMessage.success(t('common.messages.success'), t('maker.msg.success.generate'));
        } catch (error: any) {
            console.error('Error generating estimate:', error);
            setError(error.message || t('maker.msg.error.generate'));
        } finally {
            setGenerating(false);
        }
    };

    return (
        <RootDialogPage
            open={open}
            onClose={onClose}
            title={t('maker.estimates.new')}
            onAction={handleGenerateEstimate}
            actionLabel={generating ? t('action.generating') : t('maker.estimates.generate')}
            actionDisabled={generating || !selectedProjectId}
            maxWidth="sm"
            fullWidth
        >
            <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {error && <Alert severity="error">{error}</Alert>}
                <FormControl fullWidth>
                    <InputLabel>{t('maker.design.select_project')}</InputLabel>
                    <Select
                        value={selectedProjectId}
                        label={t('maker.design.select_project')}
                        onChange={(e) => setSelectedProjectId(e.target.value)}
                    >
                        <MenuItem value=""><em>None</em></MenuItem>
                        {projects.map((project) => (
                            <MenuItem key={project.id} value={project.id.toString()}>
                                {project.name} {project.description && `- ${project.description}`}
                            </MenuItem>
                        ))}
                    </Select>
                    {projects.length === 0 && <Typography variant="caption" color="error">{t('maker.msg.noProjects')}</Typography>}
                </FormControl>
            </Box>
        </RootDialogPage>
    );
};
